import React, { useState } from "react";
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "./hooks/useAdmin";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const { isAdmin, loading, user } = useAdmin();
  
  // State for email/password forms
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'google', 'forgot-password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setAuthLoading(true);
      setAuthError('');
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Signed in with Google:", user.email);
      navigate("/");
    } catch (error) {
      console.error("Google login error:", error.message);
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setAuthError('Please fill in all fields');
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log("Signed in with email:", result.user.email);
      navigate("/");
    } catch (error) {
      console.error("Email login error:", error.message);
      setAuthError(getErrorMessage(error.code));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword || !displayName) {
      setAuthError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setAuthError('Password must be at least 6 characters');
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      await updateProfile(result.user, {
        displayName: displayName
      });
      
      console.log("Registered with email:", result.user.email);
      navigate("/");
    } catch (error) {
      console.error("Email registration error:", error.message);
      setAuthError(getErrorMessage(error.code));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setAuthError('Please enter your email address');
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      setSuccessMessage('');
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
      console.log("Password reset email sent to:", email);
    } catch (error) {
      console.error("Password reset error:", error.message);
      setAuthError(getErrorMessage(error.code));
    } finally {
      setAuthLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/weak-password':
        return 'Password is too weak';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again';
      default:
        return errorCode;
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setAuthError('');
    setSuccessMessage('');
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    resetForm();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem" }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "600px", margin: "auto" }}>
      <h2>Greek Ranking Authentication</h2>
      
      {user ? (
        <div>
          <div style={{ 
            padding: "1rem", 
            marginBottom: "1rem", 
            border: "1px solid #d4edda", 
            borderRadius: "4px",
            backgroundColor: "#d4edda",
            color: "#155724"
          }}>
            <h3>✅ You are logged in</h3>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Name:</strong> {user.displayName || "Not provided"}</p>
            <p><strong>Role:</strong> {isAdmin ? "Administrator" : "Regular User"}</p>
          </div>
          
          {isAdmin && (
            <div style={{ 
              padding: "1rem", 
              marginBottom: "1rem", 
              border: "1px solid #b8daff", 
              borderRadius: "4px",
              backgroundColor: "#d1ecf1",
              color: "#0c5460"
            }}>
              <h4>🔧 Admin Access Available</h4>
              <p>You have access to admin features like uploading competitions and managing data.</p>
            </div>
          )}
          
          <button 
            onClick={handleLogout}
            style={{
              padding: "10px 20px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Sign Out
          </button>
        </div>
      ) : (
        <div className="auth-container">
          <div className="auth-tabs">
            <button 
              className={`auth-tab ${authMode === 'login' ? 'active' : ''}`}
              onClick={() => switchAuthMode('login')}
            >
              Sign In
            </button>
            <button 
              className={`auth-tab ${authMode === 'register' ? 'active' : ''}`}
              onClick={() => switchAuthMode('register')}
            >
              Register
            </button>
            <button 
              className={`auth-tab ${authMode === 'forgot-password' ? 'active' : ''}`}
              onClick={() => switchAuthMode('forgot-password')}
            >
              Forgot Password
            </button>
          </div>

          {authError && (
            <div className="auth-error">
              {authError}
            </div>
          )}

          {successMessage && (
            <div className="auth-success">
              {successMessage}
            </div>
          )}

          {authMode === 'login' ? (
            <form onSubmit={handleEmailLogin} className="auth-form">
              <h3>Sign In with Email</h3>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <button 
                type="submit" 
                className="auth-button primary"
                disabled={authLoading}
              >
                {authLoading ? 'Signing In...' : 'Sign In'}
              </button>
              <div className="forgot-password-link">
                <button 
                  type="button"
                  className="link-button"
                  onClick={() => switchAuthMode('forgot-password')}
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          ) : authMode === 'register' ? (
            <form onSubmit={handleEmailRegister} className="auth-form">
              <h3>Create Account</h3>
              <div className="form-group">
                <label htmlFor="displayName">Full Name:</label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={authLoading}
                  minLength={6}
                />
                <small>Password must be at least 6 characters</small>
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password:</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <button 
                type="submit" 
                className="auth-button primary"
                disabled={authLoading}
              >
                {authLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          ) : authMode === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <h3>Reset Your Password</h3>
              <p style={{
                color: '#666', 
                fontSize: '0.9rem', 
                marginBottom: '1.5rem', 
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={authLoading}
                />
              </div>
              <button 
                type="submit" 
                className="auth-button primary"
                disabled={authLoading}
              >
                {authLoading ? 'Sending Reset Email...' : 'Send Reset Email'}
              </button>
              <div className="back-to-login">
                <button 
                  type="button"
                  className="link-button"
                  onClick={() => switchAuthMode('login')}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          ) : null}

          <div className="auth-divider">
            <span>OR</span>
          </div>

          <button 
            onClick={handleGoogleLogin}
            className="auth-button google"
            disabled={authLoading}
          >
            <svg className="google-icon" width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {authLoading ? 'Signing In...' : 'Continue with Google'}
          </button>

          <div className="auth-info">
            <p>
              {authMode === 'login' 
                ? "Don't have an account? " 
                : "Already have an account? "
              }
              <button 
                type="button"
                className="link-button"
                onClick={() => switchAuthMode(authMode === 'login' ? 'register' : 'login')}
              >
                {authMode === 'login' ? 'Register here' : 'Sign in here'}
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
